import React, { useState } from 'react';
import { Wrench, Search, Code, Terminal, GitBranch, Globe, Monitor, Eye, HardDrive } from 'lucide-react';

interface ToolItem {
  name: string;
  category: string;
  description: string;
  destructive?: boolean;
}

const TOOL_CATALOG: ToolItem[] = [
  // Workspace
  { name: 'workspace_list', category: 'workspace', description: 'Lists contents of directory ignoring git/node_modules' },
  { name: 'workspace_switch', category: 'workspace', description: 'Switches active workspace directory' },
  { name: 'workspace_snapshot', category: 'workspace', description: 'Generates structural tree snapshot of workspace' },
  { name: 'read_file', category: 'workspace', description: 'Reads file contents with line offset/pagination & binary check' },
  { name: 'search_text', category: 'workspace', description: 'Fast regex search across files in project' },
  { name: 'search_files', category: 'workspace', description: 'Finds files matching glob or filename pattern' },
  { name: 'apply_patch', category: 'workspace', description: 'Applies atomic patches with Shadow Stash backup & AST validation' },

  // Git
  { name: 'git_status', category: 'git', description: 'Retrieves structured staged, unstaged, and untracked status' },
  { name: 'git_diff', category: 'git', description: 'Returns Git diff for unstaged or staged changes' },
  { name: 'git_log', category: 'git', description: 'Shows recent Git commit history with author and message' },

  // Dev
  { name: 'project_dev', category: 'dev', description: 'Launches background dev server (npm run dev, etc.)' },
  { name: 'project_test', category: 'dev', description: 'Runs test runner (vitest, jest, pytest) and summarizes results' },
  { name: 'project_lint', category: 'dev', description: 'Runs eslint/ruff and parses lint diagnostics' },
  { name: 'project_typecheck', category: 'dev', description: 'Runs tsc/mypy type checker and extracts error locations' },
  { name: 'project_build', category: 'dev', description: 'Runs project build command and captures output' },

  // Shell
  { name: 'shell', category: 'shell', description: 'Executes command in workspace with SafeGuard AST protection' },
  { name: 'process_list', category: 'shell', description: 'Lists active background processes launched by OpenAITunnel' },
  { name: 'process_poll_log', category: 'shell', description: 'Polls stdout/stderr stream from background process' },
  { name: 'process_send_input', category: 'shell', description: 'Sends interactive stdin to running background process' },
  { name: 'process_terminate', category: 'shell', description: 'Gracefully terminates background process' },

  // Codex
  { name: 'codex_run', category: 'codex', description: 'Delegates complex sub-agent tasks to local Codex CLI' },

  // Browser CDP
  { name: 'dom_cdp_connect', category: 'browser', description: 'Connects to Chrome instance with remote debugging port' },
  { name: 'dom_cdp_navigate', category: 'browser', description: 'Navigates Chrome tab to target URL' },
  { name: 'dom_cdp_inspect', category: 'browser', description: 'Inspects DOM tree, CSS styles, and accessibility nodes' },
  { name: 'dom_cdp_interact', category: 'browser', description: 'Performs click, typing, scroll, or hover actions on elements' },
  { name: 'dom_cdp_eval', category: 'browser', description: 'Evaluates JavaScript in browser context' },
  { name: 'dom_cdp_screenshot', category: 'browser', description: 'Captures full page or element screenshot' },

  // OS Automation & Input
  { name: 'accessibility_dump_tree', category: 'os_automation', description: 'Dumps UI Automation accessibility tree of windows' },
  { name: 'accessibility_find_element', category: 'os_automation', description: 'Finds UI element in active desktop application' },
  { name: 'accessibility_invoke', category: 'os_automation', description: 'Invokes button/menu item in desktop app' },
  { name: 'accessibility_set_value', category: 'os_automation', description: 'Enters text in native desktop input fields' },
  { name: 'input_event_mouse', category: 'os_automation', description: 'Simulates mouse move, click, drag, and scroll events' },
  { name: 'input_event_keyboard', category: 'os_automation', description: 'Simulates native keyboard typing and hotkeys' },
  { name: 'window_list', category: 'os_automation', description: 'Lists open desktop windows with handles and process IDs' },
  { name: 'window_focus', category: 'os_automation', description: 'Brings specific desktop window to foreground' },
  { name: 'window_transform', category: 'os_automation', description: 'Resizes, moves, minimizes, or maximizes windows' },
  { name: 'file_dialog_open', category: 'os_automation', description: 'Interacts with open/save native file dialogs' },

  // Vision
  { name: 'vision_capture', category: 'vision', description: 'Captures high-res screenshot of screen or window' },
  { name: 'vision_ocr', category: 'vision', description: 'Extracts text and bounding boxes from screenshot' },
  { name: 'vision_compress', category: 'vision', description: 'Compresses image for multimodal AI payloads' },

  // System
  { name: 'office_excel_read', category: 'system', description: 'Reads Excel .xlsx sheets, rows, and formulas' },
  { name: 'office_excel_write', category: 'system', description: 'Creates or modifies Excel .xlsx spreadsheets' },
  { name: 'office_word_read', category: 'system', description: 'Reads Word .docx paragraphs and tables' },
  { name: 'office_word_write', category: 'system', description: 'Creates or updates Word .docx documents' },
  { name: 'clipboard_read', category: 'system', description: 'Reads current text from system clipboard' },
  { name: 'clipboard_write', category: 'system', description: 'Writes text to system clipboard' },
  { name: 'screen_record_start', category: 'system', description: 'Starts MP4/WebM video recording of screen' },
  { name: 'screen_record_stop', category: 'system', description: 'Stops active screen recording' },
  { name: 'audio_record', category: 'system', description: 'Records audio from microphone' },
  { name: 'audio_play', category: 'system', description: 'Plays audio tone or alert sound' },
  { name: 'system_info', category: 'system', description: 'Gathers CPU, RAM, Disk, GPU, and OS metrics' },
  { name: 'health', category: 'system', description: 'Checks OpenAITunnel daemon health and memory' },
  { name: 'notification_send', category: 'system', description: 'Sends desktop toast notification' },
  { name: 'scheduler_create', category: 'system', description: 'Schedules recurring task or cron job' },
  { name: 'scheduler_list', category: 'system', description: 'Lists all scheduled jobs' },
  { name: 'web_fetch', category: 'system', description: 'Performs HTTP GET/POST to local or intranet services' }
];

export const ToolGrid: React.FC = () => {
  const [selectedCat, setSelectedCat] = useState<string>('ALL');
  const [search, setSearch] = useState<string>('');

  const categories = ['ALL', 'workspace', 'git', 'dev', 'shell', 'browser', 'os_automation', 'vision', 'system'];

  const filtered = TOOL_CATALOG.filter(t => {
    const matchesCat = selectedCat === 'ALL' || t.category === selectedCat;
    const matchesSearch = t.name.toLowerCase().includes(search.toLowerCase()) ||
                          t.description.toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {categories.map(cat => (
            <button
              key={cat}
              className={`tab-btn ${selectedCat === cat ? 'active' : ''}`}
              style={{ padding: '6px 12px', fontSize: '0.8rem' }}
              onClick={() => setSelectedCat(cat)}
            >
              {cat.toUpperCase()}
            </button>
          ))}
        </div>

        <div style={{ position: 'relative', minWidth: '260px' }}>
          <input
            type="text"
            placeholder="Search tools..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '8px 12px',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-sans)',
              fontSize: '0.85rem'
            }}
          />
        </div>
      </div>

      <div className="tool-grid">
        {filtered.map(tool => (
          <div key={tool.name} className="tool-card">
            <div className="tool-card-header">
              <span className="tool-name">{tool.name}</span>
              <span className="brand-badge" style={{ background: '#1e293b', color: '#94a3b8' }}>
                {tool.category.toUpperCase()}
              </span>
            </div>
            <p className="tool-desc">{tool.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
